/**
 * @fileoverview Centralized configuration for AI Task document upload system
 * Single source of truth for all document upload constraints and file type definitions
 * Shared between backend and frontend to ensure consistency
 */

export interface DocumentTypeConfig {
  /** MIME type for validation */
  mimeType: string;
  /** File extension (with dot) */
  extension: string;
  /** Human-readable name */
  displayName: string;
  /** Emoji icon for UI */
  icon: string;
}

export const DOCUMENT_TYPES = {
  PDF: {
    mimeType: "application/pdf",
    extension: ".pdf",
    displayName: "PDF",
    icon: "📄"
  },
  DOCX: {
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    extension: ".docx",
    displayName: "Word",
    icon: "📝"
  },
  PPTX: {
    mimeType: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    extension: ".pptx",
    displayName: "PowerPoint",
    icon: "📊"
  }
} as const;

export const ACCEPTED_DOCUMENT_MIME_TYPES = Object.values(DOCUMENT_TYPES).map(
  (doc) => doc.mimeType
);

export const ACCEPTED_DOCUMENT_EXTENSIONS_STRING = Object.values(DOCUMENT_TYPES)
  .map((doc) => doc.extension)
  .join(",");

export const DOCUMENT_FORMAT_NAMES = Object.values(DOCUMENT_TYPES)
  .map((doc) => doc.displayName)
  .join(", ");

export const DOCUMENT_SIZE_LIMITS = {
  /** Maximum file size in bytes (Mistral's limit) */
  MAX_SIZE_BYTES: 50 * 1024 * 1024, // 50 MB
  /** Maximum file size in MB */
  MAX_SIZE_MB: 50,
  /** Human-readable size string */
  MAX_SIZE_STRING: "50 MB",
  /** Threshold for using base64 vs S3 upload (backend only) */
  BASE64_THRESHOLD_BYTES: 37.5 * 1024 * 1024 // 37.5 MB
} as const;

export function getDocumentTypeByMimeType(mimeType: string): DocumentTypeConfig | null {
  const entry = Object.values(DOCUMENT_TYPES).find((doc) => doc.mimeType === mimeType);
  return entry || null;
}

export function getDocumentIcon(mimeType: string): string {
  const docType = getDocumentTypeByMimeType(mimeType);
  return docType?.icon || "📎";
}

export function isDocumentMimeTypeSupported(mimeType: string): boolean {
  return ACCEPTED_DOCUMENT_MIME_TYPES.some((type) => type === mimeType);
}
